import React, { useEffect, useState } from 'react'
import { Avatar, Box, Link, Paper, Typography } from '@mui/material'
import { type PersonBaseBasicInformation, usGetPersonPicture } from 'services'
import { generateLegalName } from 'helpers'
import { useSessionContext } from 'session'
import { useUsPrefixLuts } from 'components'

interface PersonMiniCardProps {
  person: PersonBaseBasicInformation
  filled: boolean
  withPhoto?: boolean
  fullWidth?: boolean
  onNameClick?: () => void
}

export const PersonMiniCard: React.FC<PersonMiniCardProps> = ({ person, filled, withPhoto = false, fullWidth = false, onNameClick }) => {
  const { openNewTab } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const [pictureSrc, setPictureSrc] = useState<string | undefined>(undefined)

  useEffect(() => {
    setPictureSrc(undefined)
    if (!withPhoto || !filled) return
    void usGetPersonPicture(person.id).then((value) => {
      if (value.result?.image != null) {
        setPictureSrc(`data:image/jpeg;base64,${value.result.image}`)
      }
    }).catch(() => {})
  }, [person.id, withPhoto, filled])

  const fullName = generateLegalName(person, usPrefixLutValues.prefixes)
  const initials = `${person.firstName?.charAt(0) ?? ''}${person.lastName.charAt(0)}`.toUpperCase()

  return (
    <Paper variant='outlined' sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, width: fullWidth ? '100%' : undefined, maxWidth: (theme) => (fullWidth ? 'none' : theme.spacing(50)) }}>
      {filled && (withPhoto
        ? <Avatar src={pictureSrc} alt={fullName}>{initials}</Avatar>
        : <Avatar alt={fullName} />)}
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!filled && (
          <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1.6 }}>
            Vacant
          </Typography>
        )}
        {filled && (
          <>
            <Link
              component='button'
              underline='hover'
              onClick={() => { onNameClick != null ? onNameClick() : openNewTab(`/us/editPerson/${person.id}`) }}
              sx={{ maxWidth: '100%', textAlign: 'left' }}
            >
              <Typography variant='body2' noWrap>{fullName}</Typography>
            </Link>
            <Typography variant='caption' color='text.secondary'>ID {person.id}</Typography>
          </>
        )}
      </Box>
    </Paper>
  )
}
